import { useEffect, useId, useRef, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface MobileNavProps {
  /** Whether the off-canvas drawer is open. */
  open: boolean;
  /** Called when the drawer should close (backdrop click, ESC, route change, close-button). */
  onClose: () => void;
  /** Sidebar contents — header, nav links, footer. Re-used desktop sidebar markup is fine. */
  children: ReactNode;
  /** Accessible label for the dialog, e.g. "Admin navigation". */
  ariaLabel: string;
  /** DOM id for the panel (paired with the trigger's aria-controls). */
  id?: string;
  /** Optional extra classes on the panel itself. */
  className?: string;
}

/**
 * Off-canvas mobile navigation drawer with backdrop, focus trap, ESC + backdrop
 * close, and route-change auto-close. Renders nothing above `md:` — the desktop
 * sidebar should render its own static layout independently.
 *
 * a11y:
 *  - `role="dialog"` + `aria-modal="true"` on the panel
 *  - Focus is captured on open and restored to the trigger on close.
 *  - Tab / Shift+Tab cycles through focusable elements inside the drawer.
 *  - ESC closes.
 *  - Body scroll is locked while open.
 */
export function MobileNav({
  open,
  onClose,
  children,
  ariaLabel,
  id,
  className,
}: MobileNavProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const location = useLocation();
  const labelId = useId();

  // Close on route change so navigating doesn't leave the drawer hanging open.
  useEffect(() => {
    if (open) onClose();
    // We intentionally depend only on the pathname — onClose identity is stable
    // in callers (they use a local closeSidebar). Including it would re-fire
    // every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Keyboard handlers: ESC closes, Tab traps focus inside the panel.
  useEffect(() => {
    if (!open) return;

    previouslyFocused.current =
      (document.activeElement as HTMLElement | null) ?? null;

    // Focus the first focusable element in the panel on open.
    const focusFirst = () => {
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = panel.querySelectorAll<HTMLElement>(
        'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      const first = focusables[0];
      if (first) first.focus();
      else panel.focus();
    };
    // Defer so the panel is in the DOM and visible.
    const raf = requestAnimationFrame(focusFirst);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(
        panel.querySelectorAll<HTMLElement>(
          'a, button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute("aria-hidden"));
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !panel.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKey, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = prevOverflow;
      // Restore focus to whatever opened the drawer (e.g. the hamburger).
      const prev = previouslyFocused.current;
      if (prev && typeof prev.focus === "function") prev.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="md:hidden" aria-label={ariaLabel}>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/60"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Panel */}
      <div
        ref={panelRef}
        id={id}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={labelId}
        tabIndex={-1}
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-[260px] max-w-[85vw] border-r border-border bg-surface flex flex-col shadow-2xl outline-none animate-slide-in-left",
          className,
        )}
      >
        {/* Hidden label for assistive tech (sidebar headers vary per role). */}
        <span id={labelId} className="sr-only">
          {ariaLabel}
        </span>
        {children}
      </div>
    </div>
  );
}

/**
 * Drop-in close button for use inside MobileNav children (matches the
 * look-and-feel of the inline close in each sidebar). Exported so layouts
 * can reuse the same icon button.
 */
export function MobileNavCloseButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Close navigation"
      className="md:hidden inline-flex items-center justify-center h-11 w-11 rounded-md text-muted hover:text-text hover:bg-surface2"
    >
      <X className="h-5 w-5" />
    </button>
  );
}
